/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ThirdPartyConnector, type ThirdPartyConnectorProps } from '../third-party-connector';
import { type ConnectorInfo } from '../use3pConnectorResources';

const styles = cssMap({
	container: {
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'hidden',
	},
	row: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	rowLast: {
		borderBottomStyle: 'none',
	},
});

export type ConnectorInfoListProps = {
	connectorResources: ConnectorInfo[];
	onConnectorConnected?: (productKey: string) => void;
};

export type LegacyThirdPartyConnectorListProps = {
	items: ThirdPartyConnectorProps[];
};

export type ThirdPartyConnectorListProps =
	| ConnectorInfoListProps
	| LegacyThirdPartyConnectorListProps;

const isConnectorInfoListProps = (
	props: ThirdPartyConnectorListProps,
): props is ConnectorInfoListProps => 'connectorResources' in props;

export const ThirdPartyConnectorList = (props: ThirdPartyConnectorListProps): JSX.Element => {
	const connectorItems = isConnectorInfoListProps(props)
		? props.connectorResources.map((resource) => ({
				key: resource.productKey,
				props: {
					connectorInfo: resource,
					onSuccess: () => props.onConnectorConnected?.(resource.productKey),
				} satisfies ThirdPartyConnectorProps,
			}))
		: props.items.map((item) => ({
				key: 'connectorInfo' in item ? item.connectorInfo.productKey : item.name,
				props: item,
			}));

	return (
		<Box xcss={cx(styles.container)}>
			{connectorItems.map((item, index) => (
				<Box
					key={item.key}
					xcss={cx(styles.row, index === connectorItems.length - 1 && styles.rowLast)}
				>
					<ThirdPartyConnector {...item.props} />
				</Box>
			))}
		</Box>
	);
};
